import React from 'react';
import * as Sentry from '@sentry/react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { SUPPORT_HOME } from '@/lib/permissions';
import SignIn from '@/pages/SignIn';
import MfaEnroll from '@/pages/MfaEnroll';
import MfaChallenge from '@/pages/MfaChallenge';

import ExerciseDetail from '@/pages/ExerciseDetail';
import ProgramDetail from '@/pages/ProgramDetail';
import Sessions from '@/pages/Sessions';
import Exercises from '@/pages/Exercises.jsx';
import Users from '@/pages/Users.jsx';
import MainLayout from '@/components/layout/MainLayout';
import { Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import UserActivityDashboard from '@/pages/UserActivityDashboard';
import UserActivityDetail from '@/pages/UserActivityDetail';
import B2B from '@/pages/B2B';
import SessionDetail from '@/pages/SessionDetail';
import WearableData from '@/pages/WearableData';
import WearableActivityDetail from '@/pages/WearableActivityDetail';
import AdvancedSearch from '@/pages/AdvancedSearch';
import Bookmarks from '@/pages/Bookmarks';
import SessionBuilder from '@/pages/SessionBuilder';
import LiveStudio from '@/pages/LiveStudio';
import TokenUsage from '@/pages/TokenUsage';
import EmailStudio from '@/pages/EmailStudio';
import NewSubscribers from '@/pages/NewSubscribers';
import { TooltipProvider } from '@/components/ui/tooltip';
import CommandPalette from '@/components/CommandPalette';

    function ErrorFallback({ error, resetError }) {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4 p-8 text-center bg-background">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-sm text-muted-foreground max-w-md">
            An unexpected error occurred. This has been reported automatically.
          </p>
          {error?.message && (
            <pre className="text-xs text-muted-foreground bg-muted rounded-md p-3 max-w-lg overflow-auto">
              {error.message}
            </pre>
          )}
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reload page
            </button>
            {resetError && (
              <button
                onClick={resetError}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                Try again
              </button>
            )}
          </div>
        </div>
      );
    }

    const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

    const ProtectedRoute = ({ children }) => {
      const { user, isCoach, isSupport, canAccessRoute, loading, hasTotpFactor, isAal2 } = useAuth();
      const location = useLocation();

      if (loading) {
        return (
          <div className="flex items-center justify-center h-screen">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        );
      }

      if (!user || !isCoach) {
        return <Navigate to="/signin" replace />;
      }

      // If the admin has a TOTP factor enrolled but the current session is
      // only AAL1, redirect to the MFA challenge page before granting access.
      if (hasTotpFactor && !isAal2) {
        return <Navigate to="/mfa/challenge" state={{ from: location.pathname }} replace />;
      }

      // Support staff are external: two-factor login is mandatory.
      if (isSupport && !hasTotpFactor) {
        return <Navigate to="/mfa/enroll" replace />;
      }

      if (!canAccessRoute(location.pathname)) {
        return <Navigate to={SUPPORT_HOME} replace />;
      }

      return children;
    };

    const HomeRedirect = () => {
      const { isSupport } = useAuth();
      return <Navigate to={isSupport ? SUPPORT_HOME : '/exercises'} replace />;
    };

    const ProtectedLayout = () => {
        return (
            <ProtectedRoute>
              <TooltipProvider>
                <MainLayout>
                    <Outlet />
                </MainLayout>
              </TooltipProvider>
            </ProtectedRoute>
        );
    };

    const GuestRoute = ({ children }) => {
      const { user, isCoach, loading } = useAuth();

      if (loading) {
        return (
          <div className="flex items-center justify-center h-screen">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        );
      }

      if (user && isCoach) {
        return <Navigate to="/" replace />;
      }
      
      return children;
    }

    function App() {
      return (
        <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}>
          <CommandPalette />
          <SentryRoutes>
            <Route 
              path="/signin" 
              element={
                <GuestRoute>
                  <SignIn />
                </GuestRoute>
              } 
            />
            {/* MFA routes — accessible to signed-in users regardless of AAL */}
            <Route path="/mfa/enroll" element={<MfaEnroll />} />
            <Route path="/mfa/challenge" element={<MfaChallenge />} />
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/live-studio" element={<LiveStudio />} />

              <Route path="/exercises" element={<Exercises />} />
              <Route path="/exercises/search" element={<AdvancedSearch />} />
              <Route path="/bookmarks" element={<Bookmarks />} />
              <Route path="/exercise/:id" element={<ExerciseDetail />} />
              <Route path="/program/:id" element={<ProgramDetail />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/session-builder" element={<SessionBuilder />} />
              <Route path="/new-subscribers" element={<NewSubscribers />} />
              <Route path="/user-program" element={<Users />} />
              <Route path="/user-activity" element={<UserActivityDashboard />} />
              <Route path="/user-activity/:userId" element={<UserActivityDetail />} />
              <Route path="/user-activity/:userId/wearable" element={<WearableData />} />
              <Route path="/user-activity/:userId/wearable/:activityId" element={<WearableActivityDetail />} />
              <Route path="/user-activity/:userId/session/:sessionId" element={<SessionDetail />} />
              <Route path="/b2b" element={<B2B />} />
              <Route path="/token-usage" element={<TokenUsage />} />
              <Route path="/email-studio" element={<EmailStudio />} />
            </Route>
          </SentryRoutes>
        </Sentry.ErrorBoundary>
      );
    }

    export default Sentry.withProfiler(App);
